class MetaMegaMenu extends HTMLElement {
	// Selectors and class names for reusability
	static selectors = {
		primary: '.mega-menu__primary > li',
		primaryLink: '.mega-menu__primary > li > a',
		secondaryGroup: '.mega-menu__secondary-group',
		secondaryLink: '.mega-menu__secondary > li > a',
		tertiaryGroup: '.mega-menu__tertiary-group',
		tertiaryMenu: '.mega-menu__tertiary',
		tertiaryLink: '.mega-menu__tertiary > li > a',
		image: '.mega-menu__image',
		overlay: '.mega-menu__overlay',
		container: '.mega-menu__container',
	};

	static classes = {
		active: 'is-active',
		link: 'link',
	};

	constructor() {
		super();
		this.cacheElements();
		this.attachListeners();
		this.setInitialActiveImage();
	}

	cacheElements() {
		const selectors = MetaMegaMenu.selectors;
		this.primaryLinks = this.querySelectorAll(selectors.primaryLink);
		this.primaryItems = this.querySelectorAll(selectors.primary);
		this.secondaryGroups = this.querySelectorAll(selectors.secondaryGroup);
		this.tertiaryGroups = this.querySelectorAll(selectors.tertiaryGroup);
		this.images = this.querySelectorAll(selectors.image);
		this.overlay = this.querySelector(selectors.overlay);
		this.container = this.querySelector(selectors.container);

		// Cache groups by index for fast access
		this.secondaryGroupsByIndex = {};
		this.tertiaryGroupsByIndex = {};
		this.secondaryGroups.forEach((group) => {
			const idx = group.dataset.primaryIndex;
			this.secondaryGroupsByIndex[idx] = group;
		});
		this.tertiaryGroups.forEach((group) => {
			const idx = group.dataset.primaryIndex;
			this.tertiaryGroupsByIndex[idx] = group;
		});

		// Cache secondary and tertiary links for each primary item
		this.secondaryLinksCache = new Map();
		this.tertiaryLinksCache = new Map();
		this.tertiaryMenusCache = new Map();

		this.primaryLinks.forEach((link, index) => {
			// Cache secondary links
			const secondaryLinks = this.querySelectorAll(
				`${selectors.secondaryGroup}[data-primary-index="${index}"] ${selectors.secondaryLink}`
			);
			this.secondaryLinksCache.set(index, secondaryLinks);

			// Cache tertiary links
			const tertiaryLinks = this.querySelectorAll(
				`${selectors.tertiaryGroup}[data-primary-index="${index}"] ${selectors.tertiaryLink}`
			);
			this.tertiaryLinksCache.set(index, tertiaryLinks);

			// Cache tertiary menus
			const tertiaryMenus = this.querySelectorAll(
				`${selectors.tertiaryGroup}[data-primary-index="${index}"] ${selectors.tertiaryMenu}`
			);
			this.tertiaryMenusCache.set(index, tertiaryMenus);
		});
	}

	// Helper to get the best available image for the current menu context
	getActiveImage(primaryKey, secondaryKey, tertiaryKey) {
		// Try tertiary image (most specific)
		if (
			typeof tertiaryKey !== 'undefined' &&
			typeof secondaryKey !== 'undefined' &&
			typeof primaryKey !== 'undefined'
		) {
			const tertiaryImg = this.querySelector(
				`${MetaMegaMenu.selectors.image}[data-primary-key="${primaryKey}"][data-secondary-key="${secondaryKey}"][data-tertiary-key="${tertiaryKey}"]`
			);
			if (tertiaryImg) return tertiaryImg;
		}

		// Try secondary image
		if (typeof secondaryKey !== 'undefined' && typeof primaryKey !== 'undefined') {
			const secondaryImg = this.querySelector(
				`${MetaMegaMenu.selectors.image}[data-primary-key="${primaryKey}"][data-secondary-key="${secondaryKey}"]:not([data-tertiary-key])`
			);
			if (secondaryImg) return secondaryImg;
		}

		// Try primary image (least specific)
		if (typeof primaryKey !== 'undefined') {
			const primaryImg = this.querySelector(
				`${MetaMegaMenu.selectors.image}[data-primary-key="${primaryKey}"]:not([data-secondary-key])`
			);
			return primaryImg || null;
		}
		return null;
	}

	// Utility to hide all elements matching a selector
	hideAllBySelector(selector) {
		this.querySelectorAll(selector).forEach((el) => el.classList.remove(MetaMegaMenu.classes.active));
	}

	hideAllSecondaryGroups() {
		this.hideAllBySelector(MetaMegaMenu.selectors.secondaryGroup + '.' + MetaMegaMenu.classes.active);
	}

	hideAllTertiaryGroups() {
		this.hideAllBySelector(MetaMegaMenu.selectors.tertiaryGroup + '.' + MetaMegaMenu.classes.active);
	}

	hideAllTertiaryMenus() {
		this.hideAllBySelector(MetaMegaMenu.selectors.tertiaryMenu + '.' + MetaMegaMenu.classes.active);
	}

	hideAllImages() {
		this.hideAllBySelector(MetaMegaMenu.selectors.image + '.' + MetaMegaMenu.classes.active);
	}

	setInitialActiveImage() {
		const firstPrimary = this.querySelector(MetaMegaMenu.selectors.primary);
		if (!firstPrimary) return;
		const primaryKey = firstPrimary.dataset.primaryKey;
		const primaryImage = this.getActiveImage(primaryKey);
		if (primaryImage) {
			primaryImage.classList.add(MetaMegaMenu.classes.active);
		}
	}

	attachListeners() {
		const selectors = MetaMegaMenu.selectors;
		const classes = MetaMegaMenu.classes;
		const primaryLinks = this.querySelectorAll(selectors.primaryLink);
		primaryLinks.forEach((link) => {
			const primaryKey = link.closest('li').dataset.primaryKey;
			link.addEventListener('mouseenter', () => {
				this.hideAllSecondaryGroups();
				this.hideAllTertiaryGroups();
				this.hideAllTertiaryMenus();
				this.hideAllImages();

				// Show the corresponding secondary group if it has items
				const secondaryGroup = this.querySelector(
					`${selectors.secondaryGroup}[data-primary-key="${primaryKey}"]`
				);
				if (secondaryGroup && secondaryGroup.querySelector(selectors.secondaryLink)) {
					secondaryGroup.classList.add(classes.active);
				}

				// Show the corresponding tertiary group if it has items and secondary is valid
				const tertiaryGroup = this.querySelector(
					`${selectors.tertiaryGroup}[data-primary-key="${primaryKey}"]`
				);
				if (
					tertiaryGroup &&
					tertiaryGroup.querySelector(selectors.tertiaryLink) &&
					secondaryGroup &&
					secondaryGroup.querySelector(selectors.secondaryLink)
				) {
					tertiaryGroup.classList.add(classes.active);
					const firstTertiaryMenu = tertiaryGroup.querySelector(
						`${selectors.tertiaryMenu}[data-secondary-key]`
					);
					if (firstTertiaryMenu) {
						firstTertiaryMenu.classList.add(classes.active);
					}
				} else {
					this.hideAllTertiaryGroups();
					this.hideAllTertiaryMenus();
				}

				// Show the primary item's image
				const primaryImage = this.getActiveImage(primaryKey);
				if (primaryImage) {
					primaryImage.classList.add(classes.active);
				}
			});

			// Secondary links for this primary
			const secondaryLinks = this.querySelectorAll(
				`${selectors.secondaryGroup}[data-primary-key="${primaryKey}"] ${selectors.secondaryLink}`
			);
			secondaryLinks.forEach((secondaryLink) => {
				const secondaryKey = secondaryLink.closest('li').dataset.secondaryKey;
				secondaryLink.addEventListener('mouseenter', () => {
					this.hideAllTertiaryMenus();
					this.hideAllImages();
					this.hideAllTertiaryGroups();

					const tertiaryMenu = this.querySelector(
						`${selectors.tertiaryMenu}[data-secondary-key="${secondaryKey}"]`
					);
					if (tertiaryMenu) {
						tertiaryMenu.classList.add(classes.active);
						const tertiaryGroup = tertiaryMenu.closest(selectors.tertiaryGroup);
						if (tertiaryGroup) {
							tertiaryGroup.classList.add(classes.active);
						}
					}

					// Use helper for fallback image logic
					const image = this.getActiveImage(primaryKey, secondaryKey);
					if (image) {
						image.classList.add(classes.active);
					}
				});
			});

			// Tertiary links for this primary
			const tertiaryLinks = this.querySelectorAll(
				`${selectors.tertiaryGroup}[data-primary-key="${primaryKey}"] ${selectors.tertiaryLink}`
			);
			tertiaryLinks.forEach((tertiaryLink) => {
				const parentMenu = tertiaryLink.closest(selectors.tertiaryMenu);
				const secondaryKey = parentMenu ? parentMenu.dataset.secondaryKey : undefined;
				const tertiaryKey = tertiaryLink.closest('li').dataset.tertiaryKey;

				tertiaryLink.addEventListener('mouseenter', () => {
					this.hideAllImages();

					const image = this.getActiveImage(primaryKey, secondaryKey, tertiaryKey);
					if (image) {
						image.classList.add(classes.active);
					}
				});
			});
		});
	}
}

if (!customElements.get('meta-mega-menu')) {
	customElements.define('meta-mega-menu', MetaMegaMenu);
}

class MetaMegaMenuToggle extends HTMLElement {
	// Selectors and class names for reusability
	static selectors = {
		button: 'button',
		metaMegaMenu: 'meta-mega-menu',
		overlay: '.mega-menu__overlay',
	};

	static classes = {
		active: 'is-active',
	};

	constructor() {
		super();
		const selectors = MetaMegaMenuToggle.selectors;
		this.button = this.querySelector(selectors.button);
		this.metaMegaMenu = document.querySelector(selectors.metaMegaMenu);
		this.overlay = this.metaMegaMenu.querySelector(selectors.overlay);
		this.isOpen = false;
		this.attachListeners();
	}

	attachListeners() {
		this.button.addEventListener('click', () => this.toggleMenu());
		this.overlay.addEventListener('click', () => this.closeMenu());
		document.addEventListener('keydown', this.handleKeydown.bind(this));
	}

	handleKeydown(event) {
		if (event.key === 'Escape' && this.isOpen) {
			this.closeMenu();
		}
	}

	toggleMenu() {
		this.isOpen ? this.closeMenu() : this.openMenu();
	}

	openMenu() {
		const classes = MetaMegaMenuToggle.classes;
		this.metaMegaMenu.classList.add(classes.active);
		this.button.setAttribute('aria-expanded', 'true');
		this.button.classList.add(classes.active);
		this.isOpen = true;
	}

	closeMenu() {
		const classes = MetaMegaMenuToggle.classes;
		this.metaMegaMenu.classList.remove(classes.active);
		this.button.setAttribute('aria-expanded', 'false');
		this.button.classList.remove(classes.active);
		this.isOpen = false;

		// Reset MetaMegaMenu state
		this.metaMegaMenu.hideAllSecondaryGroups();
		this.metaMegaMenu.hideAllTertiaryGroups();
		this.metaMegaMenu.hideAllTertiaryMenus();
		this.metaMegaMenu.hideAllImages();

		// Show first primary item and its corresponding groups
		const firstPrimary = this.metaMegaMenu.querySelector(MetaMegaMenu.selectors.primary);
		if (!firstPrimary) return;
		const primaryKey = firstPrimary.dataset.primaryKey;

		// Show secondary group
		const firstSecondaryGroup = this.metaMegaMenu.querySelector(
			MetaMegaMenu.selectors.secondaryGroup + '[data-primary-key]'
		);
		if (firstSecondaryGroup) {
			firstSecondaryGroup.classList.add(MetaMegaMenu.classes.active);
		}

		// Show tertiary group
		const firstTertiaryGroup = this.metaMegaMenu.querySelector(
			MetaMegaMenu.selectors.tertiaryGroup + '[data-primary-key]'
		);
		if (firstTertiaryGroup) {
			if (firstTertiaryGroup && firstTertiaryGroup.querySelector(MetaMegaMenu.selectors.tertiaryLink)) {
				firstTertiaryGroup.classList.add(MetaMegaMenu.classes.active);

				// Show first tertiary menu
				const firstTertiaryMenu = firstTertiaryGroup.querySelector(
					MetaMegaMenu.selectors.tertiaryMenu + '[data-secondary-key]'
				);
				if (firstTertiaryMenu) {
					firstTertiaryMenu.classList.add(MetaMegaMenu.classes.active);
				}
			}
		}

		// Show primary image
		const firstImage = this.metaMegaMenu.querySelector(
			MetaMegaMenu.selectors.image + `[data-primary-key="${primaryKey}"]:not([data-secondary-key])`
		);
		if (firstImage) {
			firstImage.classList.add(MetaMegaMenu.classes.active);
		}
	}
}

if (!customElements.get('meta-mega-menu-toggle')) {
	customElements.define('meta-mega-menu-toggle', MetaMegaMenuToggle);
}
